import React from 'react';
import { Search, X, Grid, List, Filter } from 'lucide-react';

/**
 * SecurityServicesFilters - Filter component for the Security Services Matrix
 * Provides search, category filtering, and view mode controls
 */
const SecurityServicesFilters = ({
  searchTerm,
  setSearchTerm,
  selectedServiceCategory,
  setSelectedServiceCategory,
  selectedFeatureCategory,
  setSelectedFeatureCategory,
  viewMode,
  setViewMode,
  resetFilters,
  serviceCategories,
  featureCategories,
  totalServices,
  totalFeatures
}) => {
  const hasActiveFilters = searchTerm || selectedServiceCategory !== 'all' || selectedFeatureCategory !== 'all';

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* Left side - Search and Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search services, features, or categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* Service Category Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={selectedServiceCategory}
              onChange={(e) => setSelectedServiceCategory(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">All Services</option>
              {serviceCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Feature Category Filter */}
          <div className="flex items-center space-x-2">
            <select
              value={selectedFeatureCategory}
              onChange={(e) => setSelectedFeatureCategory(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">All Features</option>
              {featureCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </button>
          )}
        </div>

        {/* Right side - View controls and stats */}
        <div className="flex items-center justify-between sm:justify-end space-x-4">
          {/* Results Count */}
          <div className="text-sm text-gray-600">
            <span className="font-medium">{totalServices}</span> services, 
            <span className="font-medium ml-1">{totalFeatures}</span> features
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('matrix')}
              className={`flex items-center px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === 'matrix'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid className="w-4 h-4 mr-1" />
              Matrix
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === 'cards'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4 mr-1" />
              Cards
            </button>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500">Active filters:</span>
          {searchTerm && (
            <div className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              Search: "{searchTerm}"
              <button
                onClick={() => setSearchTerm('')}
                className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {selectedServiceCategory !== 'all' && (
            <div className="flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              Service: {selectedServiceCategory}
              <button
                onClick={() => setSelectedServiceCategory('all')}
                className="ml-1 hover:bg-green-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {selectedFeatureCategory !== 'all' && (
            <div className="flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
              Feature: {selectedFeatureCategory}
              <button
                onClick={() => setSelectedFeatureCategory('all')}
                className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SecurityServicesFilters; 